import PauseIcon from "@assets/icons/pause.svg";
import PlayIcon from "@assets/icons/play.svg";
import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import _ from "lodash";
import { Slider } from "primereact/slider";
import React, { AudioHTMLAttributes, useCallback, useMemo, useRef, useState } from "react";

interface AudioPlayerProps extends AudioHTMLAttributes<HTMLAudioElement> {
	url?: string;
}

const formatDuration = (seconds: number) => {
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60)
		.toString()
		.padStart(2, "0");
	return `${mins}:${secs}`;
};

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ url, ...audioProps }: AudioPlayerProps) => {
	const { format } = useI18n();

	const [isPlaying, setIsPlaying] = useState(false);
	const [duration, setDuration] = useState(0);
	const [time, setTime] = useState(0);

	const stringTimeStamp = useMemo(() => formatDuration(time), [time]);
	const stringDuration = useMemo(() => formatDuration(duration), [duration]);

	const refAudio = useRef<HTMLAudioElement>(null);

	const handlePause = useCallback(() => {
		setIsPlaying(false);
		refAudio.current?.pause();
	}, []);

	const handlePlay = useCallback(
		(givenTime?: number) => {
			let newTime = givenTime ?? time;
			setIsPlaying(true);

			if (newTime === duration) {
				newTime = 0;
			}
			setTime(newTime);
			if (refAudio.current) {
				refAudio.current.currentTime = newTime;
			}
			refAudio.current?.play();
		},
		[duration, time],
	);

	const toggleIsPlaying = useCallback(() => {
		isPlaying ? handlePause() : handlePlay();
	}, [handlePause, handlePlay, isPlaying]);

	const handleLoadedMetadata = useCallback(() => {
		setDuration(
			isNotNil(refAudio.current?.duration) && _.isFinite(refAudio.current.duration) ? refAudio.current.duration : 0,
		);
	}, []);

	const handleEnded = useCallback(() => {
		setIsPlaying(false);
	}, []);

	const handleProgressBarClick = useCallback(
		(val: number | [number, number]) => {
			handlePause();
			if (_.isNumber(val)) {
				setTime(val);
			}
		},
		[handlePause],
	);

	const handleAudioProgress = useCallback(() => {
		if (refAudio.current) {
			setTime(refAudio.current.currentTime);
		}
	}, []);

	return (
		<>
			<Audio
				ref={refAudio}
				{...audioProps}
				onLoadedMetadata={handleLoadedMetadata}
				onTimeUpdate={handleAudioProgress}
				onEnded={handleEnded}
			>
				<source src={url} />
			</Audio>
			<StyledAudioPlayer borderColor={colors.primary25}>
				<div className="p-size-s-bold">{format("audioPlayer.description")}</div>
				<PlayerContainer>
					<IconsContainer>
						{isPlaying ? (
							<PauseIcon width={16} height={16} color={colors.grey} onClick={toggleIsPlaying} />
						) : (
							<PlayIcon width={16} height={16} color={colors.grey} onClick={toggleIsPlaying} />
						)}
					</IconsContainer>
					<SliderContainer>
						<StyledSlider
							value={time}
							max={duration}
							step={0.01}
							onChange={e => {
								handleProgressBarClick(e.value);
							}}
							onSlideEnd={({ value }) => {
								handlePlay(_.isNumber(value) ? value : undefined);
							}}
						/>
					</SliderContainer>
					<TimeStamp>
						{stringTimeStamp} / {stringDuration}
					</TimeStamp>
				</PlayerContainer>
			</StyledAudioPlayer>
		</>
	);
};

const Audio = styled.audio`
	display: none;
`;

const StyledAudioPlayer = styled.div<{ borderColor: string }>`
	width: 100%;
	margin-bottom: 16px;
	padding: 16px;
	border-radius: 8px;
	border: 1px solid;
	border-color: ${props => props.borderColor};
`;
const PlayerContainer = styled.div`
	${row(undefined, "spacebetween", "center")};
`;

const IconsContainer = styled.div`
	cursor: pointer;
	width: 16px;
	height: 16px;
`;

const SliderContainer = styled.div`
	width: calc(100% - 136px);
`;
const StyledSlider = styled(Slider)`
	--thickness: 0.75rem;
	.p-slider-handle {
		margin-top: calc(var(--thickness) / -2) !important;
		margin-left: calc(var(--thickness) / -2) !important;
		height: var(--thickness);
		width: var(--thickness);
		transition-property: var(--thickness);
		transition-duration: 0.2s;
	}
	.p-slider-handle:hover {
		--thickness: 1.25rem;
	}
`;
const TimeStamp = styled.div``;
